import { AxleIconButton } from '@/components/buttons/AxleIconButton';
import { HorizontalStack, HorizontalStackProps } from '@/components/utils/linearLayouts';
import { InputState } from '@/utils/inputState';
import { Children, ReturnComponent } from '@/utils/reactTypes';
import Clear from '@mui/icons-material/Clear';
import { TextField } from '@mui/material';
import { InputProps as StandardInputProps } from '@mui/material/Input/Input';

import { FieldWithAxleLabel, labelWithRequiredAsterisk } from './AxleLabel';

export type AxleTextFieldProps = Omit<HorizontalStackProps, 'children'> & {
    label?: string;
    enabled?: boolean;
    inputState: InputState<string>;
    inlineLabel?: boolean;
    description?: Children;
    placeholder?: string;
    multiline?: boolean;
    maxRows?: number;
    maxChars?: number;
    onEnter?: () => void;
    endAdornment?: Children;
    showClearButton?: boolean;
    _extraInputProps?: Partial<StandardInputProps>;
};

export const AxleTextField = ({
    label,
    enabled = true,
    inputState,
    inlineLabel = false,
    description,
    placeholder,
    multiline = false,
    maxRows,
    maxChars,
    onEnter,
    endAdornment,
    showClearButton = false,
    _extraInputProps = {},
}: AxleTextFieldProps): ReturnComponent => {
    if (showClearButton) {
        endAdornment = (
            <HorizontalStack fullWidth={false}>
                {endAdornment}
                <AxleIconButton
                    disabled={!enabled}
                    icon={<Clear />}
                    tooltip={'Clear'}
                    onClick={() => inputState.setInputState('')}
                />
            </HorizontalStack>
        );
    }

    const showInlineLabel = inlineLabel && label;

    return (
        <FieldWithAxleLabel
            label={label}
            inlineLabel={inlineLabel}
            required={inputState.required}
            description={description}
        >
            <TextField
                // Text components.
                label={
                    showInlineLabel
                        ? labelWithRequiredAsterisk({ label, required: inputState.required })
                        : undefined
                }
                helperText={inputState.helperText}
                placeholder={placeholder}
                error={inputState.showError}
                // Behaviour.
                disabled={!enabled}
                value={inputState.value}
                onChange={(event) => inputState.setInputState(event.target.value)}
                multiline={multiline}
                maxRows={maxRows}
                inputProps={{
                    ...(maxChars !== undefined && { maxLength: maxChars }),
                }}
                onWheel={() => {
                    // TextField with input type 'number' does weird stuff with
                    //  scrolling to change a number, so this prevents that from happening.
                    if (document.activeElement instanceof HTMLElement) {
                        document.activeElement.blur();
                    }
                }}
                onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                        onEnter?.();
                    }
                }}
                // Styling.
                fullWidth={true}
                variant={'outlined'}
                size={'small'}
                // Extra.
                InputProps={{
                    ..._extraInputProps,
                    endAdornment: endAdornment,
                }}
            />
        </FieldWithAxleLabel>
    );
};
